import React, { useState, useEffect } from 'react';
import { getPublishedBlogs } from '../services/blogService';
import BlogCard from '../components/blog/BlogCard';
import '../components/blog/blogStyles.css';

const BlogList = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);

  const limit = 6; // 2x3 grid

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async (loadMore = false) => {
    setLoading(true);
    setError(null);
    try {
      const { blogs: newBlogs, lastDoc: newLastDoc } = await getPublishedBlogs(limit, loadMore ? lastDoc : null);
      if (loadMore) {
        setBlogs(prev => [...prev, ...newBlogs]);
      } else {
        setBlogs(newBlogs);
      }
      setLastDoc(newLastDoc);
      setHasMore(newBlogs.length === limit);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = () => {
    if (hasMore && !loading) {
      fetchBlogs(true);
    }
  };

  const filteredBlogs = blogs.filter(blog => {
    const matchesSearch = blog.title.toLowerCase().includes(search.toLowerCase()) ||
                          blog.content.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = !category || blog.category === category;
    const matchesTags = selectedTags.length === 0 || selectedTags.every(tag => blog.tags?.includes(tag));
    return matchesSearch && matchesCategory && matchesTags;
  });

  const allTags = [...new Set(blogs.flatMap(blog => blog.tags || []))];
  const allCategories = [...new Set(blogs.map(blog => blog.category).filter(Boolean))];

  const toggleTag = (tag) => {
    setSelectedTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  };

  return (
    <div className="blog-list-container">
      <div className="container">
        {/* Header */}
        <div className="blog-list-header">
          <h1 className="blog-list-title">Insights & Innovation</h1>
          <p className="blog-list-subtitle">Discover the latest in sustainable biotech</p>
        </div>

        {/* Filters */}
        <div className="blog-filters">
          <div className="row g-3">
            <div className="col-lg-4">
              <div className="search-input-wrapper">
                <svg className="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 21L16.5 16.5M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <input
                  type="text"
                  className="form-control search-input"
                  placeholder="Search articles..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
            <div className="col-lg-3">
              <select
                className="form-select category-select"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                <option value="">All Categories</option>
                {allCategories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            <div className="col-lg-5">
              <div className="tag-filters">
                {allTags.slice(0, 6).map(tag => (
                  <button
                    key={tag}
                    className={`tag-chip ${selectedTags.includes(tag) ? 'active' : ''}`}
                    onClick={() => toggleTag(tag)}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="alert alert-danger premium-alert" role="alert">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 9V11M12 15H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            {error}
          </div>
        )}

        {/* Blog Grid */}
        <div className={`blog-grid ${filteredBlogs.length === 2 ? 'grid-2-cards' : ''}`}>
          {filteredBlogs.map(blog => (
            <BlogCard key={blog.id} blog={blog} />
          ))}
        </div>

        {/* Load More */}
        {hasMore && (
          <div className="load-more-container">
            <button
              className="btn btn-load-more"
              onClick={loadMore}
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="spinner-border spinner-border-sm me-2" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  Loading...
                </>
              ) : (
                <>
                  Load More Articles
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M7 13L12 18L17 13M7 6L12 11L17 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </>
              )}
            </button>
          </div>
        )}

        {/* Loading Skeleton */}
        {loading && !hasMore && (
          <div className="loading-skeleton">
            <div className="skeleton-grid">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="skeleton-card">
                  <div className="skeleton-image"></div>
                  <div className="skeleton-content">
                    <div className="skeleton-title"></div>
                    <div className="skeleton-text"></div>
                    <div className="skeleton-meta"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && filteredBlogs.length === 0 && (
          <div className="empty-state">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 12H15M9 16H15M17 21H7C5.89543 21 5 20.1046 5 19V5C5 3.89543 5.89543 3 7 3H12.5858C12.851 3 13.1054 3.10536 13.2929 3.29289L18.7071 8.70711C18.8946 8.89464 19 9.149 19 9.41421V19C19 20.1046 18.1046 21 17 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <h3>No articles found</h3>
            <p>Try adjusting your search or filters</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogList;